import { FC, useMemo, useState } from 'react';
import { useSearchRevenueSchedules } from '../../../api/revenueService';
import {
  MAccordion,
  MBox,
  MFlex,
  MPageLoader,
  MText,
} from '../../../components/Monetize';
import { ExpandCollapseButton } from '../../../components/Monetize/ExpandCollapseButton';
import { LatestScheduleSection } from '../../../components/RevenueRecognition/LatestScheduleSection';
import { PreviousScheduleAccordionItem } from '../../../components/RevenueRecognition/PreviousScheduleAccordionItem';
import { RevenueContractAccordion } from '../../../components/RevenueRecognition/RevenueContractAccordion';

interface RevenueContractScheduleTabProps {
  revenueContractId: string;
}

export const RevenueContractScheduleTab: FC<
  RevenueContractScheduleTabProps
> = ({ revenueContractId }) => {
  const { data, isLoading } = useSearchRevenueSchedules(
    { revenueContractId },
    { enabled: !!revenueContractId, refetchOnWindowFocus: false },
  );

  const sortedSchedules = useMemo(() => {
    const schedules = data?.content ?? [];
    // Latest version first; entries without a version sort to the end.
    return [...schedules].sort(
      (left, right) =>
        (right.version ?? -Infinity) - (left.version ?? -Infinity),
    );
  }, [data]);

  const [latestSchedule, ...previousSchedules] = sortedSchedules;

  // v3's Accordion.Root tracks expanded items by string `value`, not a
  // numeric index - each schedule's Accordion.Item `value` is its
  // stringified render position.
  const [expandedValues, setExpandedValues] = useState<string[]>([]);
  const safeExpandedValues = useMemo(
    () =>
      expandedValues.filter((value) => {
        const index = Number(value);
        return index >= 0 && index < previousSchedules.length;
      }),
    [expandedValues, previousSchedules.length],
  );
  const allExpanded =
    previousSchedules.length > 0 &&
    safeExpandedValues.length === previousSchedules.length;

  if (!data && isLoading) {
    return <MPageLoader />;
  }

  if (!latestSchedule) {
    return (
      <MBox pt={4}>
        <MText color="tGray.darkPurple" fontSize="sm" fontStyle="italic">
          No revenue schedules found for this revenue contract.
        </MText>
      </MBox>
    );
  }

  return (
    <MBox data-testid="revenue-contract-schedule" pt={4}>
      <LatestScheduleSection
        schedule={latestSchedule}
        revenueContractId={revenueContractId}
        currency="USD"
      />

      {previousSchedules.length > 0 && (
        <MBox mt={6}>
          <MAccordion multiple variant="bottomBorder">
            <RevenueContractAccordion
              title={`Inactive Revenue Schedules (${previousSchedules.length})`}
            >
              {previousSchedules.length >= 2 && (
                <MFlex justify="flex-start" my={2}>
                  <ExpandCollapseButton
                    isExpanded={allExpanded}
                    onChange={(expanded) =>
                      setExpandedValues(
                        expanded
                          ? previousSchedules.map((_schedule, index) =>
                              String(index),
                            )
                          : [],
                      )
                    }
                  />
                </MFlex>
              )}
              <MAccordion
                multiple
                value={safeExpandedValues}
                onValueChange={({ value }) => setExpandedValues(value)}
              >
                {previousSchedules.map((schedule, index) => (
                  <PreviousScheduleAccordionItem
                    key={schedule.id}
                    value={String(index)}
                    schedule={schedule}
                  />
                ))}
              </MAccordion>
            </RevenueContractAccordion>
          </MAccordion>
        </MBox>
      )}
    </MBox>
  );
};
